//=============================================================================
//                       <--- Developed By Icon Team --->
//=============================================================================

// npm packages
import React, { useState, useCallback, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import * as moment from "moment";
import { pdf } from "@react-pdf/renderer";

// custom pages
import report from "apis/Report/Report";
import Context from "context/context";
import BenefitMetricCard from 'views/Dashboard/Dashboard/Components/PointsMetrics'
import BenefitStatementPDF from "./PDF/BenefitStatementPDF";

// material-ui
import Button from "@material-ui/core/Button";

// material-ui icons
import GetAppIcon from "@material-ui/icons/GetApp";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useTranslation } from 'react-i18next';

//Analytics
import { logEvent } from "helpers/analytics";

const benefitConfig = [
    {
        label: "Your Points Earnings",
        rows: [
            { key: "henrySchein", text: "5X Points from Henry Schein Products" },
            { key: "equipment", text: "3X Points from Equipment" },
            { key: "supplierBonus", text: "Exclusive Bonus Points" },
            { key: "baseX", text: "1.5X Points on All Other Purchases" },
        ],
    },
    {
        label: "Your Shipping Savings",
        rows: [{ key: "merchandiseShipping", text: "On Merchandise" }],
    },
    {
        label: "Your Service Savings",
        rows: [{ key: "serviceLaborDiscount", text: "44% Service Labor Discount" }],
    },
    {
        label: "Your Analytics Savings",
        rows: [
            { key: "practiceAnalysis", text: "Henry Schein Practice Analysis", retail: "($699 retail)" },
            { key: "feeAnalysis", text: "Henry Schein Fee Analysis", retail: "($149 retail)" },
            { key: "hsRevenueAnalysis", text: "HS Revenue Analysis", retail: "($599 retail)" },
        ],
    },
    {
        label: "Supplies Savings",
        small: "(Last 12 Months)",
        rows: [{ key: "suppliesSavings", text: "Savings on Dental Merchandise Purchases" }],
    },
];

export const defaultData = {
    date: "9/29/2026",
    customerName: "Altadonna, Darren | 4391299",
    consultant: "BRIAN EVANS",
    memberSince: "5/5/2025",
    points: {
        balance: { points: "59,876", value: "$299" },
        redeemed: { points: "102,300", value: "$512" },
    },
    benefits: [
        {
            label: "Your Points Earnings",
            rows: [
                { text: "5X Points from Henry Schein Products", value: "$351" },
                { text: "3X Points from Equipment", value: "$0" },
                { text: "Exclusive Bonus Points", value: "$0" },
                { text: "1.5X Points on All Other Purchases", value: "$326" },
            ],
        },
        {
            label: "Your Shipping Savings",
            rows: [{ text: "On Merchandise", value: "$820" }],
        },
        {
            label: "Your Service Savings",
            rows: [
                { text: "44% Service Labor Discount", value: "$633" },
                { text: "1 Sterilizer Rental Included", value: "$0" },
                { text: "Technical Advisor Line", value: "$0" },
                {
                    text: "1 Preventative Maintenance Visit",
                    retail: "($600 retail)",
                    note: "Exclusive to Annual Subscribers",
                    value: "$0",
                },
            ],
        },
        {
            label: "Your Analytics Savings",
            rows: [
                { text: "Henry Schein Practice Analysis", retail: "($699 retail)", value: "$0" },
                { text: "Henry Schein Fee Analysis", retail: "($149 retail)", value: "$149" },
            ],
        },
    ],
    total: "$2,279",
};

// colors / font (same family and palette as BenefitValueCard and the mockup)
const NAVY = "#0b1245";
const TITLE_BLUE = "#0b2bd1";
const HEAD_BLUE = "#0b1fa8";
const MUTED = "#6b7280";
const BLUE = "#0a6ed1";
const CARD_BG = "#eaf4fe";
const CARD_BORDER = "#4aa3ee";
const ROW_BG = "#f3f8ff";
const ROW_BORDER = "#d6e4f5";
const FONT = "'DM Sans', sans-serif";

const money = (n) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(n || 0));

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// backend can send one or many emails in one string, e.g. "a@x.com,b@x.com"
// -> returns only the valid ones (empty array when nothing usable)
const parseRepEmails = (value) =>
    String(value || "")
        .split(/[,;]+/)
        .map((e) => e.trim())
        .filter((e) => EMAIL_REGEX.test(e));

// backend can send the text "null" for a missing name -> treat as empty
const cleanText = (value) => {
    const s = String(value ?? "").trim();
    return s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined" ? s : "";
};

// backend sends subscriptionType: 'Annual' (ST1) | 'Trial' (ST2) | 'Monthly' (ST0)
const PLAN_LABELS = {
    Annual: "Annual Subscriber",
    Trial: "Trial Subscriber",
    Monthly: "Monthly Subscriber",
};

const outlineButtonStyle = {
    textTransform: "none",
    color: BLUE,
    borderColor: BLUE,
    background: "#ffffff",
    fontFamily: FONT,
    fontSize: 16,
    padding: "6px 22px",
    borderRadius: 6,
};

const countText = (n, one, many) => `${Number(n || 0).toLocaleString("en-US")} ${Number(n) === 1 ? one : many}`;

export default function ViewMyBenefitStatement({ loyaltyId, page, setDisableDropdown }) {
    const { state } = useContext(Context);
    const history = useHistory();
    const { viewMyBenefitStatement } = report();
    const { t } = useTranslation();
    const [states, setstates] = useState({
        loading: false,
        data: null,
        downloadPending: false,
    });

    // Result shared by the BenefitValueCard (single API call):
    //   undefined -> still loading
    //   null      -> request failed
    //   { eligible: false, availableFrom } -> Signature, not more than 1 year
    //   {...data} -> eligible data (or Thrive sample values)
    const [benefit, setBenefit] = useState(undefined);

    const acaLoaded = !!state?.ACA && Object.keys(state.ACA).length > 0;
    const isThriveUnlocked = state?.ACA?.unlocked_flag === true;
    const isSignature = state?.ACA?.signature_flag === true && !state?.ACA?.unlocked_flag;
    const isStartup = state?.ACA?.startup_flag === true;
    const isThrive = !isStartup && !isThriveUnlocked && !isSignature;

    const resolvedLoyaltyId = loyaltyId || state?.ALA?.loyalty_id;

    useEffect(() => {
        // Log page view
        logEvent({
            event_type: "page_view",
            event_name: "View my Statements",
            user_id: state?.userInfo?.user_id,
            session_id: state?.userInfo?.session_id,
        });
    }, []);

    const setStates = useCallback((newState) => {
        setstates((prev) => ({ ...prev, ...newState }));
    }, []);

    // ---------- download (existing functionality, unchanged) ----------
    async function handleViewMyBenefitStatement() {
        setStates({ loading: true });

        let url;

        try {
            const payload = {
                lId: resolvedLoyaltyId,
                shipto: state?.ACA?.customer_account_number,
            };

            const res = await viewMyBenefitStatement(payload);

            if (res?.message !== "Ok" || !res?.data) {
                console.error("Invalid API response:", res);
                return;
            }

            const { data } = res;

            const benefits = benefitConfig.map((section) => ({
                ...section,
                rows: section.rows.map((row) => ({
                    ...row,
                    value: data?.benefits?.[row.key] ?? 0,
                })),
            }));

            const stateData = {
                customerName: data?.customerName,
                billTo: data?.billTo,
                memberSince: data?.memberSince,
                signatureSince: data?.signatureSince,
                date: data?.date,
                points: data?.points,
                benefits,
                total: data?.total,
            };

            // Generate PDF using the API response data
            const blob = await pdf(<BenefitStatementPDF data={stateData} />).toBlob();

            // Trigger automatic download
            url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `BenefitStatement ${moment().format("MM-DD-YYYY_h-mm-A")}.pdf`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            setStates({ data: stateData });
        } catch (error) {
            console.error("Benefit statement PDF error:", error);
        } finally {
            if (url) {
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }

            setStates({ loading: false, downloadPending: false });
        }
    }

    // ---------- page title ----------
    const titleStyle = {
        fontSize: 32,
        fontWeight: 700,
        color: TITLE_BLUE,
        lineHeight: 1.15,
        letterSpacing: "-0.01em",
    };

    // nothing until the customer account is known (avoids a wrong flash of Thrive / message)
    if (!acaLoaded) {
        return <div style={{ marginTop: 20 }} />;
    }

    // ---------- not available: friendly message ----------
    const supported = isSignature || isThrive;
    const signatureTooEarly = isSignature && benefit && benefit.eligible === false;
    const signatureLoadFailed = isSignature && benefit === null;

    if (!supported || signatureTooEarly || signatureLoadFailed) {
        let heading;
        let body;

        if (!supported) {
            heading = t("Your benefit statement isn't available yet");
            body = t(
                "Benefit statements are available for Thrive Rewards and Thrive Signature members. Once your account is enrolled in Thrive Signature, your savings will be displayed here."
            );
        } else if (signatureTooEarly) {
            const availableMoment = benefit?.availableFrom
                ? moment(benefit.availableFrom, "MM/DD/YYYY")
                : null;
            const availableText =
                availableMoment && availableMoment.isValid()
                    ? availableMoment.format("MMMM D, YYYY")
                    : "";

            heading = t("Your Signature Benefit Statement is coming soon");
            body = availableText
                ? `${t("Your statement becomes available after your first year in Signature. It will be ready for you on")} ${availableText}.`
                : t("Your statement becomes available after your first year in Signature. We'll have your savings ready for you then.");
        } else {
            heading = t("We couldn't load your benefit statement");
            body = t("Something went wrong on our end. Please try again in a few minutes.");
        }

        return (
            <div style={{ marginTop: 20, fontFamily: FONT, color: NAVY }}>
                <div style={titleStyle}>{t("My Benefit Statement")}</div>

                <div
                    style={{
                        marginTop: 24,
                        padding: "34px 28px",
                        background: CARD_BG,
                        border: `1.5px solid ${CARD_BORDER}`,
                        borderRadius: 12,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                    }}
                >
                    <div
                        style={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            background: "#ffffff",
                            border: `1px solid ${ROW_BORDER}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <InfoOutlinedIcon style={{ fontSize: 36, color: BLUE }} />
                    </div>

                    <div style={{ fontSize: 26, fontWeight: 700, color: NAVY, marginTop: 16, letterSpacing: "-0.01em" }}>
                        {heading}
                    </div>

                    <div style={{ fontSize: 18, color: "#1f2937", marginTop: 8, maxWidth: 620, lineHeight: 1.45 }}>
                        {body}
                    </div>

                    <Button
                        variant="outlined"
                        onClick={() => history.push("/dashboard/dashboard")}
                        style={{ ...outlineButtonStyle, marginTop: 22 }}
                    >
                        {t("Back to Dashboard")}
                    </Button>
                </div>
            </div>
        );
    }

    // ---------- header values ----------
    const hasData = !!benefit && benefit.eligible !== false;

    const practiceName =
        cleanText(benefit?.practiceName) ||
        cleanText(benefit?.customerName ? String(benefit.customerName).split(" | ")[0] : "") ||
        cleanText(state?.ACA?.customer_name) ||
        "";

    // plan type (Annual / Trial / Monthly) is Signature only
    const planLabel =
        isSignature && PLAN_LABELS[benefit?.subscriptionType]
            ? t(PLAN_LABELS[benefit.subscriptionType])
            : "";

    const subtitle = [practiceName, planLabel].filter(Boolean).join(" · ");

    // last 12 months period shown at the top right
    const endMoment = benefit?.date ? moment(benefit.date, "MM/DD/YYYY") : null;
    const periodText =
        endMoment && endMoment.isValid()
            ? `${endMoment.clone().subtract(1, "year").add(1, "day").format("MMM D, YYYY")} \u2013 ${endMoment.format("MMM D, YYYY")}`
            : "";

    // sales rep: Signature only. Shown only when the backend sends at least one valid email.
    // The backend may send several emails separated by commas.
    const repEmails = parseRepEmails(benefit?.salesRep);
    const showRepBanner = isSignature && hasData && repEmails.length > 0;

    // ---------- category rows (same wording as the mockup) ----------
    const categoryRows = hasData
        ? isSignature
            ? [
                  {
                      key: "points",
                      title: "Points earned",
                      sub: countText(benefit.totalPoints, t("point"), t("points")),
                      amount: benefit.pointsEarning,
                  },
                  {
                      key: "shipping",
                      title: "Free shipping",
                      sub: countText(benefit.merchOrderCount, t("order"), t("orders")),
                      amount: benefit.shippingSavings,
                  },
                  {
                      key: "supplies",
                      title: "Supplies",
                      sub: `${t("Signature pricing on")} ${countText(benefit.suppliesOrderCount, t("order"), t("orders"))}`,
                      amount: benefit.suppliesSavings,
                  },
              ]
            : [
                  { key: "points", title: "Points earned", sub: "", amount: benefit.pointsEarning },
                  { key: "shipping", title: "Free shipping", sub: "", locked: true },
                  { key: "supplies", title: "Supplies", sub: "", locked: true },
              ]
        : [];

    return (
        <div style={{ marginTop: 20, fontFamily: FONT, color: NAVY }}>

            {/* ---------- top: title + period + download ---------- */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 12,
                }}
            >
                <div>
                    <div style={titleStyle}>
                        {isSignature ? t("Your Signature Benefit Statement") : t("Your Thrive Benefit Statement")}
                    </div>
                    {subtitle && (
                        <div style={{ fontSize: 17, color: MUTED, marginTop: 6, textTransform: "uppercase" }}>
                            {subtitle}
                        </div>
                    )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
                    {periodText && (
                        <div style={{ fontSize: 15, color: MUTED }}>{periodText}</div>
                    )}
                    <Button
                        variant="outlined"
                        onClick={handleViewMyBenefitStatement}
                        disabled={states.loading}
                        startIcon={<GetAppIcon style={{ fontSize: 20 }} />}
                        style={outlineButtonStyle}
                    >
                        <span style={{ textDecoration: "underline" }}>
                            {states.loading ? t("Generating PDF...") : t("Download PDF")}
                        </span>
                    </Button>
                </div>
            </div>

            {states.loading && (
                <div style={{ color: "red", marginTop: 8 }}>
                    *Please wait don't reload the page, system is processing
                </div>
            )}

            {/* ---------- value card (shared component) ---------- */}
            <BenefitMetricCard
                customerAccountNumber={state?.ACA?.customer_account_number}
                loyaltyId={resolvedLoyaltyId}
                program={isSignature ? "signature" : "thrive"}
                showViewStatementLink={false}
                showPeriod={false}
                onDataLoaded={setBenefit}
            />

            {/* ---------- contact rep (Signature only, only when backend sends rep email(s)) ---------- */}
            {showRepBanner && (
                <div
                    style={{
                        marginTop: 20,
                        padding: "14px 20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                        background: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <PersonOutlineIcon style={{ fontSize: 30, color: NAVY }} />
                        <div>
                            <div style={{ fontSize: 17, fontWeight: 700, color: NAVY }}>
                                {t("Questions about your benefits?")}
                            </div>
                            <div style={{ fontSize: 15, color: MUTED, wordBreak: "break-word" }}>
                                {t("Contact your rep")}: {repEmails.join(", ")}
                            </div>
                        </div>
                    </div>

                    <Button
                        variant="outlined"
                        href={`mailto:${repEmails.join(",")}`}
                        style={outlineButtonStyle}
                    >
                        {t("Contact my rep")}
                    </Button>
                </div>
            )}

            {/* ---------- explore your savings by category ---------- */}
            {hasData && (
                <div style={{ marginTop: 28 }}>
                    <div style={{ fontSize: 26, fontWeight: 700, color: HEAD_BLUE, letterSpacing: "-0.01em" }}>
                        {t("Explore your savings by category")}
                    </div>
                    <div style={{ fontSize: 16, color: MUTED, marginTop: 4 }}>
                        {isSignature
                            ? t("Select a category to see how your savings and activity were calculated.")
                            : t("Here's what you've earned with Thrive Rewards. Free shipping and Supplies savings come with Signature.")}
                    </div>

                    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                        {categoryRows.map((row) => (
                            <div
                                key={row.key}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: "16px 24px",
                                    background: ROW_BG,
                                    border: `1px solid ${ROW_BORDER}`,
                                    borderRadius: 8,
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
                                    <span style={{ fontSize: 21, fontWeight: 700, color: HEAD_BLUE }}>
                                        {t(row.title)}
                                    </span>
                                    {row.sub && (
                                        <span style={{ fontSize: 16, color: MUTED }}>· {row.sub}</span>
                                    )}
                                </div>

                                {row.locked ? (
                                    <div style={{ fontSize: 15, fontWeight: 700, color: MUTED }}>
                                        {t("Available with Signature")}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 26, fontWeight: 700, color: NAVY, letterSpacing: "-0.02em" }}>
                                        {money(row.amount)}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}